import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Quiet, Note, C, errText } from "./ui.jsx";
import CallDriver from "./CallDriver.jsx";
import { CallsSaid, ScheduledCalls } from "./LoadPage.jsx";

/**
 * One driver: their truck and trailer, whether Alfred may call them, their open loads, and their
 * calls. The schematic draws the Drivers list; a driver's own page is where its Open goes.
 */

export const consentLine = (d) => (d.consent
  ? "Agreed to Alfred's calls."
  : "Has not agreed to Alfred's calls, so Alfred does not call them.");

export function DriverView({ company, stage, data, error, open, setOpen, onCalled, onCancel, busy, msg }) {
  if (error) return <Shell company={company} stage={stage}><Back to="/carrier/dispatch/drivers">Drivers</Back><Quiet>{error}</Quiet></Shell>;
  if (!data) return <Shell company={company} stage={stage}><Back to="/carrier/dispatch/drivers">Drivers</Back><Quiet>Loading…</Quiet></Shell>;
  const { driver: d, loads = [], calls = [], scheduled = [] } = data;
  const equipment = [d.truck ? `Truck ${d.truck}` : null, d.trailerNo ? `Trailer ${d.trailerNo}` : null, d.trailerType || null].filter(Boolean).join(" · ");
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/dispatch/drivers">Drivers</Back>
      <Title sub={[equipment || "No truck or trailer on file", d.phone].filter(Boolean).join(" · ")}>{d.name}</Title>
      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 10 }}>
        <p style={{ margin: 0, fontSize: 16 }}>{consentLine(d)}{d.persona ? " An AI plays this driver, for demonstrations." : ""}</p>
        {loads.length === 0 ? <Quiet>No open loads.</Quiet> : loads.map((l) => (
          <Link key={l.id} to={`/carrier/dispatch/loads/${l.id}`} style={{ fontSize: 16, fontWeight: 500, textDecoration: "none" }}>{`On ${l.ref} →`}</Link>
        ))}
      </Card>
      <CallDriver driver={d} load={null} onDone={onCalled} />
      {scheduled.length > 0 && (
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Set for later</h2>
          <ScheduledCalls scheduled={scheduled} onCancel={onCancel} busy={busy} />
          <Note msg={msg} />
        </Card>
      )}
      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{`Calls with ${String(d.name).split(/\s+/)[0]}`}</h2>
        <CallsSaid calls={calls} open={open} setOpen={setOpen} />
      </Card>
      <p style={{ margin: 0, fontSize: 14, color: C.muted }}>Changes to a driver's details go through MuleWorks.</p>
    </Shell>
  );
}

export default function DriverPage() {
  const { carrier } = useCarrier();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  // Only the newest answer is shown (see LoadPage).
  const seq = useRef(0);
  const load = useCallback(() => {
    const n = ++seq.current;
    return freight.dispatchDriver(id).then((d) => { if (n === seq.current) setData(d); })
      .catch((e) => { if (n === seq.current) setError(e?.status === 404 ? "No such driver, or not one of yours." : errText(e)); });
  }, [id]);
  useEffect(() => { setData(null); setError(null); load(); }, [load]);
  const onCancel = async (sid) => {
    setBusy(true); setMsg(null);
    try { await freight.cancelScheduled(sid); await load(); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <DriverView company={carrier.name} stage={carrier.stage} data={data} error={error} open={open} setOpen={setOpen} onCalled={() => load()}
    onCancel={onCancel} busy={busy} msg={msg} />;
}
